import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { MessageType } from 'models/Message';
import { Button, Collapsible } from 'ncoded-component-library';
import { ExtractPropTypes } from 'types';
import { t } from 'i18next';
import { getArrayLength, getHasElementsInArray, getIsCurrentUser } from 'utils';
import { AxiosResponse } from 'axios';
import api from 'api';
import MessageFile from 'components/MessageFiles/components/MessageFile';
import SoftSignalIcon from 'components/SoftSignalIcon';
import DocumentViewContext from 'providers/DocumentView/DocumentView.context';
import SentTicksIcon from 'icons/SentTicks.icon';
import useActionOnAtCopy from 'hooks/useActionOnAtCopy';
import useDeleteMessage from 'hooks/useDeleteMessage';
import TrashIcon from 'icons/Trash.icon';
import ReactionPopup from 'components/ReactionPopup';
import { ReactionType, ReactionUsers } from 'models/Reactions';
import ReactionList from 'components/ReactionList';
import { Q_AND_A_SIDES } from 'router/subrouters/DataRoom/models/QAndA';
import {
  getActiveReaction,
  getHasBeenSeen,
  getHaveAllSeen,
  getReactionUsers,
  getSeenLabel,
} from '../../helpers';
import MessagingContext from '../../../../providers/Messaging/Messaging.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useDisplayMessage from 'hooks/useDisplayChatMessage';
import { AvatarWithInitials } from 'components/AvatarWithInitials';
import useChatMessageIdentity from '../../../../hooks/useChatMessageIdentity';
import MessageListingPreview from '../MessageListingPreview';

import './ChatMessage.styles.scss';

type ChatMessageProps = {
  className?: string;
  messageId: string;
  disabled: boolean;
  prevMessage: MessageType | null;
  onDeleteMessageRequest: (messageId: string) => void;
  updateMessage: (message: Partial<MessageType>) => void;
} & Omit<MessageType, '_id'> &
  ExtractPropTypes<typeof Collapsible>;

const ChatMessage: React.FC<ChatMessageProps> = (props) => {
  const {
    messageId,
    className,
    text,
    createdAt,
    showDate,
    files,
    isInterestContext,
    softSignal,
    dataRoomNode,
    dataRoomNodeName,
    seenBy,
    isPulseCheck,
    isDeleted,
    reactions,
    disabled,
    sessionId,
    sentBy,
    isSentBySellRepresentative,
    prevMessage,
    isShareListingMessage,
    listing,
    onDeleteMessageRequest,
    updateMessage,
    ...rest
  } = props;

  const { isSuperAdmin, currentUser } = useContext(CurrentUserContext);
  const { openNode } = useContext(DocumentViewContext);
  const { currentSession, userSide } = useContext(MessagingContext);

  const dataRoomId = currentSession?.deal?.dataRoomId;
  const dataRoomName = currentSession?.deal?.name;

  const getMessageIdentity = useChatMessageIdentity(currentSession);

  const { isMyMessage, senderKey, avatar } = useMemo(
    () => getMessageIdentity({ sentBy, isSentBySellRepresentative }),
    [getMessageIdentity, sentBy, isSentBySellRepresentative],
  );

  const isSameSenderAsPrev = useMemo(
    () =>
      !!prevMessage && getMessageIdentity(prevMessage).senderKey === senderKey,
    [getMessageIdentity, prevMessage, senderKey],
  );

  const showSeen = useMemo(
    () => !isDeleted && (isSuperAdmin || isMyMessage),
    [isDeleted, isMyMessage, isSuperAdmin],
  );

  const seenLabel = useMemo(
    () => getSeenLabel(seenBy, currentSession),
    [seenBy, currentSession],
  );

  const hasBeenSeen = useMemo(() => getHasBeenSeen(seenBy), [seenBy]);

  const haveAllSeen = useMemo(
    () => getHaveAllSeen(seenBy, currentSession),
    [seenBy, currentSession],
  );

  const activeReaction = useMemo(
    () => getActiveReaction(reactions),
    [reactions],
  );

  const reactionUsers = useMemo(
    () => getReactionUsers(reactions, currentSession, currentUser),
    [reactions, currentSession, currentUser],
  );

  const thumbsUp = getArrayLength(reactionUsers.thumbsUp);
  const thumbsDown = getArrayLength(reactionUsers.thumbsDown);
  const questionMark = getArrayLength(reactionUsers.questionMark);

  const classes = classNames(
    'chat-message',
    {
      'chat-message--sent': isMyMessage,
      'chat-message--received': !isMyMessage,
      'chat-message--seen': showSeen,
      'chat-message--deleted': isDeleted,
    },
    className,
  );

  const canDeleteMessage = useDeleteMessage({ createdAt, hasBeenSeen });

  const areActionsDisabled = useMemo(
    () => disabled || isDeleted,
    [disabled, isDeleted],
  );

  const showCanDeleteMessage = useMemo(
    () =>
      !areActionsDisabled && canDeleteMessage && getIsCurrentUser(sentBy?._id),
    [areActionsDisabled, canDeleteMessage, sentBy],
  );

  const showCanReactToMessage = useMemo(
    () => !areActionsDisabled && !isMyMessage,
    [areActionsDisabled, isMyMessage],
  );

  const getActionOnAtCopy = useActionOnAtCopy();

  const { getIsEmpty, renderText } = useDisplayMessage();

  const isEmpty = getIsEmpty(text);

  const hasFiles = useMemo(() => getHasElementsInArray(files), [files]);

  const renderedFiles = useMemo(
    () =>
      files?.map(({ chatSessionFileId, name, originalType, type, url }) => (
        <MessageFile
          key={chatSessionFileId}
          id={chatSessionFileId}
          name={name}
          type={originalType || type}
          url={url}
          dataRoomId={dataRoomId}
          dataRoomName={dataRoomName}
          onFilePreview={(id: string) =>
            api.messages.previewMessageFile(sessionId, id)
          }
        />
      )),
    [files, dataRoomId, dataRoomName, sessionId],
  );

  const onReact = useCallback(
    async (nextReaction: ReactionType) => {
      api.messages.addMessageReaction(messageId, nextReaction);

      const isRemoving = nextReaction === activeReaction;

      const reactionsWithoutMine = (reactions ?? []).filter(
        ({ userId }) => !getIsCurrentUser(userId),
      );

      updateMessage({
        _id: messageId,
        reactions: isRemoving
          ? reactionsWithoutMine
          : [
              ...reactionsWithoutMine,
              {
                userId: currentUser?._id,
                reaction: nextReaction,
                isSellSide: userSide === Q_AND_A_SIDES.SELL,
              },
            ],
      });
    },
    [
      messageId,
      activeReaction,
      reactions,
      currentUser,
      userSide,
      updateMessage,
    ],
  );

  if (!hasFiles && isEmpty && !isDeleted) return null;

  return (
    <div className="chat-message__container" data-message-id={`${messageId}`}>
      <Collapsible
        {...rest}
        className={classes}
        trigger={
          <>
            <div className="chat-message__content-wrapper">
              {!isMyMessage ? (
                isSameSenderAsPrev ? (
                  <div className="empty-avatar" />
                ) : (
                  <AvatarWithInitials {...avatar} />
                )
              ) : null}

              <div className="chat-message__content">
                {isInterestContext && (
                  <p className="chat-message__content__interest-context">
                    {t('interestContext')}
                  </p>
                )}

                {softSignal && (
                  <div className="chat-message__content__soft-signal">
                    {isPulseCheck && <p>{t('pulseCheck')}:</p>}

                    {(dataRoomNode || dataRoomNodeName) && (
                      <p>{t('softSignal')}:</p>
                    )}
                    {dataRoomNode ? (
                      <Button
                        variant="link"
                        onClick={() =>
                          openNode(dataRoomNode, {
                            dataRoomName,
                            userSide,
                            ownerId: currentSession?.deal?.userId,
                            dealRoomId: currentSession?.deal?._id,
                          })
                        }
                      >
                        {dataRoomNode?.name}
                      </Button>
                    ) : (
                      dataRoomNodeName && <p>{dataRoomNodeName}</p>
                    )}

                    <SoftSignalIcon softSignal={softSignal} />
                  </div>
                )}

                <div className="chat-message__content__text">
                  {isDeleted ? (
                    t('thisMessageWasDeleted')
                  ) : (
                    <>
                      {showCanDeleteMessage && (
                        <Button
                          icon={TrashIcon}
                          variant="icon"
                          className="delete-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteMessageRequest(messageId);
                          }}
                        />
                      )}

                      {showCanReactToMessage && (
                        <ReactionPopup
                          activeReaction={activeReaction}
                          onReact={onReact}
                        />
                      )}

                      {renderText(text)}

                      {hasFiles && (
                        <div className="chat-message__content__text__files">
                          {renderedFiles}
                        </div>
                      )}

                      {isShareListingMessage && (
                        <MessageListingPreview listing={listing} />
                      )}

                      {showSeen && (
                        <SentTicksIcon
                          className={classNames({ seen: haveAllSeen })}
                        />
                      )}
                    </>
                  )}

                  <ReactionList
                    thumbsUp={thumbsUp}
                    thumbsDown={thumbsDown}
                    questionMark={questionMark}
                    getReactionUsers={() =>
                      Promise.resolve({
                        data: reactionUsers,
                      } as AxiosResponse<ReactionUsers>)
                    }
                  />
                </div>
              </div>

              {isMyMessage ? (
                isSameSenderAsPrev ? (
                  <div className="empty-avatar" />
                ) : (
                  <AvatarWithInitials {...avatar} />
                )
              ) : null}
            </div>
          </>
        }
      >
        <span
          className="chat-message__time"
          onClick={(e) => e.stopPropagation()}
        >
          {getActionOnAtCopy('sentOn', createdAt)}
        </span>
        {showSeen && (
          <span
            className="chat-message__time"
            onClick={(e) => e.stopPropagation()}
          >
            {seenLabel}
          </span>
        )}
      </Collapsible>

      {!!showDate && (
        <div className="chat-message__date">
          <span className="chat-message__date__line" />
          <span className="chat-message__date__content">{showDate}</span>
        </div>
      )}
    </div>
  );
};

export default React.memo(ChatMessage);
